import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, User, Globe, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";

const PILLARS = [
    { icon: User, title: "Consistent voice across platforms", body: "Your brand voice — stored, enforced, and applied to every post. LinkedIn, X, Instagram, Reddit — all sound like you." },
    { icon: TrendingUp, title: "Posting cadence that builds authority", body: "The algorithm rewards consistency. Lakshmi makes it easy to post daily without spending hours writing." },
    { icon: Globe, title: "Reach across all platforms", body: "One idea, four platforms. LinkedIn for professional reach. X for real-time conversations. Instagram for visual storytelling. Reddit for community." },
    { icon: Zap, title: "Ideas → posts in under 60 seconds", body: "The gap between having an idea and publishing it is where most personal brands die. Lakshmi closes that gap." },
];

const SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Build your personal brand with AI — Lakshmi for Indian professionals",
    "description": "How Indian professionals on LinkedIn, X, Instagram and Reddit use Lakshmi to build their personal brand with AI-generated content in their own voice.",
    "url": "https://hilakshmi.com/use-cases/personal-branding",
    "author": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "publisher": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "keywords": "personal branding AI tool India, build personal brand LinkedIn, AI personal brand builder"
};

export default function UseCasePersonalBrand() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA);
        document.head.appendChild(s);
        document.title = "Build Your Personal Brand with AI — Lakshmi | India's Personal Brand Tool";
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", "Build your personal brand on LinkedIn, X, Instagram and Reddit with Lakshmi — India's AI personal brand tool. Write posts in your voice automatically.");
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">

                <div className="flex flex-col lg:flex-row items-center gap-12 mb-16">
                    <div className="flex-1 text-center lg:text-left">
                        <div className="lk-label mb-3">Use case — Personal Branding</div>
                        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-6">
                            Build your personal brand <span className="lk-gradient-text">with AI</span>
                        </h1>
                        <p className="text-[var(--lk-muted)] text-base sm:text-lg max-w-xl mb-8">
                            Indian professionals who consistently post on LinkedIn get 5x more profile views, 3x more connection requests, and 2x more inbound opportunities. Lakshmi makes consistency effortless.
                        </p>
                        <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                            Start building my brand — free <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    {/* Stats visual */}
                    <div className="flex-shrink-0 w-full max-w-xs">
                        <picture>
                            <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                            <img src="/image/lakshmi_nobg_opt.png" alt="Lakshmi AI personal brand tool"
                                className="w-48 mx-auto lk-float mb-4" loading="lazy"
                                style={{ filter: "drop-shadow(0 0 30px rgba(124,58,237,0.35))" }} />
                        </picture>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { stat: "5x", label: "More profile views" },
                                { stat: "3x", label: "More connection requests" },
                                { stat: "60s", label: "To write a post" },
                                { stat: "4", label: "Platforms at once" },
                            ].map(s => (
                                <div key={s.stat} className="lk-border lk-surface rounded-xl p-4 text-center">
                                    <div className="font-bold text-2xl lk-gradient-text">{s.stat}</div>
                                    <div className="text-xs text-[var(--lk-muted)] mt-0.5">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mb-16">
                    {PILLARS.map((p, i) => (
                        <motion.div key={p.title}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            viewport={{ once: true }}
                            className="lk-border lk-surface rounded-2xl p-6"
                        >
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                                style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.25)" }}>
                                <p.icon className="w-5 h-5 text-[var(--lk-primary)]" />
                            </div>
                            <h2 className="font-semibold mb-2">{p.title}</h2>
                            <p className="text-sm text-[var(--lk-muted)] leading-relaxed">{p.body}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Who it's for */}
                <div className="lk-border lk-surface rounded-2xl p-8 mb-12">
                    <h2 className="font-semibold text-xl mb-6">Built for Indian professionals who want to grow</h2>
                    <div className="grid sm:grid-cols-3 gap-4 text-sm text-[var(--lk-muted)]">
                        {[
                            { role: "Startup founders", detail: "Share your build-in-public journey. Document lessons, wins, and pivots." },
                            { role: "Engineers & product managers", detail: "Share technical insights and career lessons that attract top opportunities." },
                            { role: "Sales leaders", detail: "Build trust with prospects before the first call. Turn expertise into inbound leads." },
                            { role: "Consultants & coaches", detail: "Turn every client engagement into a content story that brings the next client." },
                            { role: "Job seekers", detail: "A strong LinkedIn presence gets you noticed before you apply." },
                            { role: "Anyone building a career", detail: "Your personal brand is the leverage that every professional needs in 2026." },
                        ].map(item => (
                            <div key={item.role} className="lk-border rounded-xl p-4">
                                <div className="font-medium text-[var(--lk-text)] mb-1">{item.role}</div>
                                <div>{item.detail}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="text-center">
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                        Start building my personal brand <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
